import { useState } from 'react';
import {
  DsAlert,
  DsButton,
  DsInput,
  DsModal,
  DsSegmented,
  type DsSegmentedOption,
  DsTextarea,
} from '@/modules/internal-v2/ds';
import {
  useCreateContactList,
  useUpdateContactList,
} from '@/api/contact-list/contact-list.hooks';
import type {
  ContactList,
  ContactListKind,
} from '@/api/contact-list/contact-list.types';

interface ContactListModalProps {
  /** The list being edited, or `null` to create a new one. */
  list: ContactList | null;
  onClose: () => void;
  /** Called with the saved list after a successful create/update. */
  onSaved?: (list: ContactList) => void;
}

const KIND_OPTIONS: DsSegmentedOption<ContactListKind>[] = [
  { value: 'customer', label: 'Kunden' },
  { value: 'supplier', label: 'Lieferanten' },
];

/**
 * Create / edit dialog for a Verteiler's **metadata** — name, description,
 * and (on create) the immutable `kind` (Kunden vs Lieferanten). Membership is
 * managed on the Verteiler detail page, not here.
 */
export function ContactListModal({ list, onClose, onSaved }: ContactListModalProps) {
  const isEdit = list != null;
  const [name, setName] = useState(list?.name ?? '');
  const [description, setDescription] = useState(list?.description ?? '');
  const [kind, setKind] = useState<ContactListKind | null>(list?.kind ?? null);

  const handleSaved = (saved: ContactList) => {
    onSaved?.(saved);
    onClose();
  };

  const createMutation = useCreateContactList({ onSuccess: handleSaved });
  const updateMutation = useUpdateContactList({ onSuccess: handleSaved });
  const isSaving = createMutation.isPending || updateMutation.isPending;
  const failed = createMutation.isError || updateMutation.isError;

  const canSave = !!name.trim() && (isEdit || kind != null);

  const handleSave = () => {
    if (!canSave || isSaving) return;
    const trimmed = name.trim();
    const desc = description.trim();
    if (isEdit) {
      updateMutation.mutate({
        listId: list.id,
        body: { name: trimmed, description: desc },
      });
    } else {
      // canSave guarantees kind != null in create mode.
      createMutation.mutate({
        kind: kind!,
        name: trimmed,
        description: desc,
        contactEmployeeIds: [],
      });
    }
  };

  return (
    <DsModal
      open
      onClose={onClose}
      title={isEdit ? 'Verteiler bearbeiten' : 'Neuer Verteiler'}
      footer={
        <>
          <DsButton variant="ghost" onClick={onClose} disabled={isSaving}>
            Abbrechen
          </DsButton>
          <DsButton
            variant="primary"
            onClick={handleSave}
            disabled={!canSave || isSaving}
          >
            {isSaving ? 'Speichern…' : 'Speichern'}
          </DsButton>
        </>
      }
    >
      <div className="vt-form">
        {failed && (
          <DsAlert severity="error">
            Der Verteiler konnte nicht gespeichert werden.
          </DsAlert>
        )}
        {!isEdit && (
          <div className="vt-field">
            <span className="vt-label">Typ</span>
            <DsSegmented
              options={KIND_OPTIONS}
              value={kind}
              onChange={setKind}
              ariaLabel="Verteiler-Typ"
            />
          </div>
        )}
        <label className="vt-field">
          <span className="vt-label">Name</span>
          <DsInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Notebook-Händler Süd"
            maxLength={200}
            autoFocus
          />
        </label>
        <label className="vt-field">
          <span className="vt-label">Beschreibung (optional)</span>
          <DsTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="Wofür dieser Verteiler genutzt wird"
          />
        </label>
      </div>
    </DsModal>
  );
}
